import { useEffect, useMemo, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { Loader2, PencilLine, Plus, Trash2 } from 'lucide-react';
import { cn, showToast } from '@my/ui';
import {
  tmsApi,
  type Invoice,
  type InvoiceLineOverride,
  type VatRoundingMethodValue,
} from '@my/tms-api';
import {
  useChargeCostDefinitions,
  useInvoice,
  useOrdersForInvoicing,
  useUpdateDraftInvoice,
} from '@my/tms-api/hooks';
import {
  useOrganisation,
  useOrganisationAccountingCategories,
  useOrganisationNominals,
  useOrganisationTaxRates,
} from '@my/directory-api/hooks';
import type { OrganisationTaxRate } from '@my/directory-api';
import { DocumentPdf } from '@/features/settings/documents/render/DocumentPdf';
import { useInvoiceDocumentSource } from '../lib/useInvoiceDocumentSource';
import { chargePayer } from '../lib/payer';
import { ForeignVatNotice } from './ForeignVatNotice';

/**
 * Edit a DRAFT invoice or proforma under its row on the Summary tab: the
 * header, and each line's amount, VAT, nominal and category; remove a line
 * (its charge goes back to Create invoices) or add one — a charge still
 * un-invoiced on the same orders, or an ad-hoc line.
 *
 * <p>
 * The customer, currency and number stay: a different customer is a different
 * document. VAT, nominal or category changed from what the line already had is
 * a hand change — the line keeps its ✎ and the audit trail records the edit.
 * Every line needs a VAT rate and a nominal before it saves, as on raise.
 * </p>
 */
export function DraftInvoiceEditor({
  invoiceId,
  organisationId,
  onCancel,
  onDone,
}: {
  invoiceId: string;
  organisationId: string | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const query = useInvoice(invoiceId);
  if (query.isPending) {
    return (
      <Shell>
        <p className="py-4 text-center text-xs text-slate-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
          Loading invoice…
        </p>
      </Shell>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Shell>
        <p className="py-4 text-center text-xs text-red-600">Could not load this invoice.</p>
      </Shell>
    );
  }
  return <Editor invoice={query.data} organisationId={organisationId} onCancel={onCancel} onDone={onDone} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="border-y-2 border-violet-200 bg-violet-50/40 p-3">{children}</div>;
}

type DraftLine = {
  key: string;
  /** Existing line id; absent for a line added in this edit. */
  id?: string;
  sourceChargeCostId?: string | null;
  orderNumber: string | null;
  description: string;
  adhoc: boolean;
  net: number;
  taxRateId: string;
  nominal: string;
  category: string;
  products: string | null;
  vatTreatmentKey: string | null;
  /** The values before this edit — what an override is measured from. */
  was: { taxRateId: string; nominal: string; category: string };
  /** Already marked as changed by hand before this edit. */
  overridden: boolean;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

function Editor({
  invoice,
  organisationId,
  onCancel,
  onDone,
}: {
  invoice: Invoice;
  organisationId: string | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const organisationQuery = useOrganisation(organisationId);
  const roundingMethod: VatRoundingMethodValue = organisationQuery.data?.vatRoundingMethod ?? 'Total';
  const categoriesEnabled = organisationQuery.data?.useAccountingCategories ?? false;
  const taxRatesQuery = useOrganisationTaxRates(organisationId);
  const nominalsQuery = useOrganisationNominals(organisationId);
  const categoriesQuery = useOrganisationAccountingCategories(categoriesEnabled ? organisationId : null);
  const ordersQuery = useOrdersForInvoicing(organisationId);
  const chargeDefsQuery = useChargeCostDefinitions(organisationId);
  const save = useUpdateDraftInvoice(organisationId);
  const docSource = useInvoiceDocumentSource(organisationId);
  const iso = invoice.currencyIsoCode;

  const rates = useMemo(
    () =>
      (taxRatesQuery.data ?? [])
        .filter((r) => r.status === 'Active' && r.invoiceType !== 'Purchases')
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [taxRatesQuery.data],
  );
  const salesNominals = useMemo(
    () => (nominalsQuery.data ?? []).filter((n) => n.type === 'Sales' && n.status === 'Active'),
    [nominalsQuery.data],
  );
  const categories = useMemo(
    () => (categoriesEnabled ? (categoriesQuery.data ?? []).filter((c) => c.status === 'Active') : null),
    [categoriesEnabled, categoriesQuery.data],
  );

  // Header.
  const [issueDate, setIssueDate] = useState(invoice.issueDate ?? '');
  const [dueDate, setDueDate] = useState(invoice.dueDate ?? '');
  const [po, setPo] = useState(invoice.poNumber ?? '');
  const [message, setMessage] = useState(invoice.message ?? '');
  const [emailMessage, setEmailMessage] = useState(invoice.emailMessage ?? '');

  // Lines.
  const initial = useMemo<DraftLine[]>(
    () =>
      invoice.lines
        .slice()
        .sort((a, b) => a.sequence - b.sequence)
        .map((l) => {
          const was = {
            taxRateId: l.taxRateId ?? '',
            nominal: l.salesNominal ?? '',
            category: l.accountingCategoryCode ?? '',
          };
          return {
            key: l.id,
            id: l.id,
            sourceChargeCostId: l.sourceChargeCostId,
            orderNumber: l.orderNumber ?? null,
            description: l.description,
            adhoc: !l.sourceChargeCostId,
            net: l.net,
            ...was,
            products: l.products ?? null,
            vatTreatmentKey: l.vatTreatmentKey ?? null,
            was,
            overridden: !!l.overridesJson,
          };
        }),
    [invoice.lines],
  );
  const [lines, setLines] = useState<DraftLine[]>(initial);
  useEffect(() => setLines(initial), [initial]);
  const patch = (key: string, p: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...p } : l)));

  // Charges that can still go on: un-invoiced charges of the orders already
  // on this invoice that this customer pays for, not already listed.
  const orderIds = useMemo(
    () => new Set(invoice.lines.map((l) => l.orderId).filter((id): id is string => !!id)),
    [invoice.lines],
  );
  const listed = new Set(lines.map((l) => l.sourceChargeCostId).filter(Boolean));
  const available = (ordersQuery.data ?? [])
    .filter((o) => orderIds.has(o.id))
    .flatMap((o) =>
      o.charges
        .filter((c) => chargePayer(o, c) === invoice.customerPartyId)
        .map((c) => ({ charge: c, orderNumber: o.orderNumber })),
    )
    .filter(({ charge }) => !listed.has(charge.id));
  const [toAdd, setToAdd] = useState('');

  const defaultRateId = (orderNumber: string | null) =>
    lines.find((l) => l.orderNumber === orderNumber && l.taxRateId)?.taxRateId ??
    rates.find((r) => r.taxType === 'Standard')?.id ??
    '';

  const addCharge = () => {
    const found = available.find((a) => a.charge.id === toAdd);
    if (!found) return;
    const def = (chargeDefsQuery.data ?? []).find((d) => d.id === found.charge.chargeCostDefinitionId);
    const proposal = {
      taxRateId: defaultRateId(found.orderNumber),
      nominal: def?.salesNominal ?? '',
      category: def?.accountingCategoryCode ?? '',
    };
    setLines((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        sourceChargeCostId: found.charge.id,
        orderNumber: found.orderNumber,
        description: found.charge.description,
        adhoc: false,
        net: found.charge.net,
        ...proposal,
        products: null,
        vatTreatmentKey: null,
        was: proposal,
        overridden: false,
      },
    ]);
    setToAdd('');
  };
  const addAdhoc = () => {
    const proposal = { taxRateId: defaultRateId(null), nominal: '', category: '' };
    setLines((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        orderNumber: null,
        description: '',
        adhoc: true,
        net: 0,
        ...proposal,
        products: null,
        vatTreatmentKey: null,
        was: proposal,
        overridden: false,
      },
    ]);
  };

  const rateById = useMemo(() => new Map((taxRatesQuery.data ?? []).map((r) => [r.id, r])), [taxRatesQuery.data]);
  const pctOf = (id: string) => rateById.get(id)?.taxPercentage ?? 0;
  const subtotal = round2(lines.reduce((s, l) => s + l.net, 0));
  const vatTotal = useMemo(() => {
    if (roundingMethod === 'Item')
      return round2(lines.reduce((s, l) => s + round2((l.net * pctOf(l.taxRateId)) / 100), 0));
    const byRate = new Map<number, number>();
    for (const l of lines) byRate.set(pctOf(l.taxRateId), (byRate.get(pctOf(l.taxRateId)) ?? 0) + l.net);
    return round2([...byRate.entries()].reduce((s, [rate, net]) => s + round2((net * rate) / 100), 0));
    // pctOf reads rateById, listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, roundingMethod, rateById]);

  const missing = lines.flatMap((l) => {
    const gaps = [
      ...(l.taxRateId ? [] : ['VAT rate']),
      ...(l.nominal ? [] : ['nominal']),
      ...(categories && !l.category ? ['category'] : []),
      ...(l.adhoc && !l.description.trim() ? ['description'] : []),
    ];
    return gaps.length ? [`${l.description || 'New line'}: ${gaps.join(' & ')}`] : [];
  });
  const blocker = lines.length === 0 ? 'An invoice needs at least one line.' : missing[0] ?? null;

  const rateText = (id: string) => {
    const r: OrganisationTaxRate | undefined = rateById.get(id);
    return r ? `${r.name} (${r.taxPercentage}%)` : id ? 'unknown rate' : null;
  };

  const submit = async () => {
    if (blocker) return;
    try {
      const saved = await save.mutateAsync({
        invoiceId: invoice.id,
        issueDate: issueDate || null,
        dueDate: dueDate || null,
        poNumber: po.trim() || null,
        message: message.trim() || null,
        emailMessage: emailMessage.trim() || null,
        roundingMethod,
        lines: lines.map((l) => {
          const overrides: InvoiceLineOverride[] = [];
          if (l.taxRateId !== l.was.taxRateId)
            overrides.push({ field: 'Vat', from: rateText(l.was.taxRateId), to: rateText(l.taxRateId) });
          if (l.nominal !== l.was.nominal)
            overrides.push({ field: 'Nominal', from: l.was.nominal || null, to: l.nominal || null });
          if (l.category !== l.was.category)
            overrides.push({ field: 'Category', from: l.was.category || null, to: l.category || null });
          return {
            id: l.id ?? null,
            sourceChargeCostId: l.id ? null : (l.sourceChargeCostId ?? null),
            description: l.adhoc ? l.description.trim() : null,
            salesNominal: l.nominal || null,
            accountingCategoryCode: l.category || null,
            vatTreatmentKey: l.vatTreatmentKey,
            products: l.products,
            net: l.net,
            taxRatePct: pctOf(l.taxRateId),
            taxRateId: l.taxRateId || null,
            // Only a CHANGE of an existing line's value is a hand change; a
            // new line's first values are its proposal.
            overrides: l.id ? overrides : [],
          };
        }),
      });
      // Re-render and replace the stored PDF — the old one shows the old lines.
      try {
        const template = docSource.templateFor(saved.docType);
        if (template) {
          const blob = await pdf(<DocumentPdf template={template} data={docSource.buildData(saved)} />).toBlob();
          await tmsApi.uploadInvoicePdf(saved.id, blob);
        }
      } catch {
        // ignore — recreated from the layout on download
      }
      showToast({ type: 'success', message: `${saved.number} saved.` });
      onDone();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error && err.message ? err.message : 'Could not save the draft.',
      });
    }
  };

  const input =
    'rounded-sm border border-slate-200 bg-white px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-violet-400';

  return (
    <Shell>
      <p className="mb-2 rounded-md bg-violet-100/70 px-3 py-1.5 text-[11px] font-medium text-violet-800">
        Editing {invoice.docType === 'Proforma' ? 'proforma' : 'draft'} {invoice.number} for {invoice.customerName}.
        Removing a line puts its charge back in Create invoices.
      </p>

      <div className="mb-2 grid grid-cols-2 gap-2 text-xs md:grid-cols-5">
        <Field label="Issue date">
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={input} />
        </Field>
        <Field label="Due date">
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={input} />
        </Field>
        <Field label="PO number">
          <input value={po} maxLength={64} onChange={(e) => setPo(e.target.value)} className={input} />
        </Field>
        <Field label="Printed message">
          <input value={message} onChange={(e) => setMessage(e.target.value)} className={input} />
        </Field>
        <Field label="Email note">
          <input value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} className={input} />
        </Field>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <ul className="divide-y divide-slate-100">
          {lines.map((l) => {
            const changed =
              l.overridden ||
              (!!l.id && (l.taxRateId !== l.was.taxRateId || l.nominal !== l.was.nominal || l.category !== l.was.category));
            return (
              <li key={l.key} className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs">
                <span className="w-24 shrink-0 text-[11px] tabular-nums text-emerald-700">{l.orderNumber || 'Ad-hoc'}</span>
                {l.adhoc ? (
                  <input
                    value={l.description}
                    placeholder="Description…"
                    onChange={(e) => patch(l.key, { description: e.target.value })}
                    className={cn(input, 'min-w-0 flex-1')}
                  />
                ) : (
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
                    {l.description}
                    {!l.id && <span className="ml-1 text-[10px] font-normal text-violet-600">new</span>}
                  </span>
                )}
                <select
                  aria-label="Sales nominal"
                  value={l.nominal}
                  onChange={(e) => patch(l.key, { nominal: e.target.value })}
                  className={cn(input, 'w-28', !l.nominal && 'border-amber-300 text-amber-700')}
                >
                  <option value="">Nominal…</option>
                  {salesNominals.map((n) => (
                    <option key={n.id} value={n.code}>
                      {n.code}
                      {n.description ? ` — ${n.description}` : ''}
                    </option>
                  ))}
                  {l.nominal && !salesNominals.some((n) => n.code === l.nominal) && (
                    <option value={l.nominal}>{l.nominal}</option>
                  )}
                </select>
                {categories && (
                  <select
                    aria-label="Accounting category"
                    value={l.category}
                    onChange={(e) => patch(l.key, { category: e.target.value })}
                    className={cn(input, 'w-28', !l.category && 'border-amber-300 text-amber-700')}
                  >
                    <option value="">Category…</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.code}>
                        {c.code} — {c.name}
                      </option>
                    ))}
                    {l.category && !categories.some((c) => c.code === l.category) && (
                      <option value={l.category}>{l.category}</option>
                    )}
                  </select>
                )}
                <select
                  aria-label="VAT rate"
                  value={l.taxRateId}
                  onChange={(e) => patch(l.key, { taxRateId: e.target.value })}
                  className={cn(input, 'w-36', !l.taxRateId && 'border-amber-300 text-amber-700')}
                >
                  <option value="">VAT rate…</option>
                  {rates.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.taxPercentage}% · {r.name}
                    </option>
                  ))}
                  {l.taxRateId && !rates.some((r) => r.id === l.taxRateId) && (
                    <option value={l.taxRateId}>{rateText(l.taxRateId)} (inactive)</option>
                  )}
                </select>
                <input
                  type="number"
                  step="0.01"
                  aria-label="Net"
                  value={l.net}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    patch(l.key, { net: Number.isFinite(n) ? round2(n) : 0 });
                  }}
                  className={cn(input, 'w-24 text-right tabular-nums')}
                />
                <span className="w-20 shrink-0 text-right font-semibold tabular-nums text-slate-800">
                  {money(l.net * (1 + pctOf(l.taxRateId) / 100), iso)}
                </span>
                <span className="w-4 shrink-0">
                  {changed && <PencilLine className="h-3.5 w-3.5 text-violet-600" aria-label="Changed by hand" />}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${l.description || 'line'}`}
                  title={l.sourceChargeCostId ? 'Remove — the charge goes back to Create invoices' : 'Remove'}
                  onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                  className="text-slate-300 hover:text-red-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-xs">
          <select
            value={toAdd}
            onChange={(e) => setToAdd(e.target.value)}
            disabled={available.length === 0}
            className={cn(input, 'min-w-[16rem]')}
          >
            <option value="">
              {available.length === 0 ? 'No other un-invoiced charges on these orders' : 'Add a charge from these orders…'}
            </option>
            {available.map(({ charge, orderNumber }) => (
              <option key={charge.id} value={charge.id}>
                {orderNumber} · {charge.description} · {money(charge.net, iso)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={addCharge}
            disabled={!toAdd}
            className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" /> Add charge
          </button>
          <button
            type="button"
            onClick={addAdhoc}
            className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
          >
            <Plus className="h-3 w-3" /> Ad-hoc line
          </button>
        </div>
      </div>

      <ForeignVatNotice currency={iso} orgCurrency={organisationQuery.data?.currencyIsoCode} vatTotal={vatTotal} />
      <div className="mt-2 flex flex-wrap items-center justify-end gap-4 text-xs">
        {blocker && <span className="mr-auto text-[11px] text-amber-700">{blocker}</span>}
        <span className="text-slate-500">
          Subtotal <span className="tabular-nums text-slate-700">{money(subtotal, iso)}</span>
        </span>
        <span className="text-slate-500">
          VAT <span className="tabular-nums text-slate-700">{money(vatTotal, iso)}</span>
        </span>
        <span className="font-semibold text-slate-800">
          Total <span className="tabular-nums">{money(subtotal + vatTotal, iso)}</span>
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={save.isPending}
          className="rounded-sm px-2.5 py-1 font-medium text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={save.isPending || !!blocker}
          title={blocker ?? undefined}
          className="inline-flex items-center gap-1 rounded-sm bg-violet-600 px-3 py-1 font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save draft
        </button>
      </div>
    </Shell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function money(v: number, iso: string | null | undefined): string {
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: iso || 'EUR' }).format(v);
  } catch {
    return v.toFixed(2);
  }
}
